import { GridEnrichedColDef, GridRowParams, GridActionsCellItem } from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useStore } from '../../../../hooks/useStore';
import { ContentHead } from '../../../../lib/components';
import { useDialog } from '../../../../lib/components/compound/dialogs/confirmationDialog/useDialog';
import { ContentBox } from '../../../../lib/components/compound/pages/contentBox';
import { EditIcon, DeleteIcon } from '../../../../lib/icons';
import { AddButton } from '../shared/addButton';
import { CompanyTable } from '../shared/companyTable';
import { ConfirmDeleteDialog } from '../shared/confirmDelete';

export const CompanyCostCentersData = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyStore } = useStore();
  const dialog = useDialog<string>();

  const costCenters =
    companyStore.costCenters?.map((costCenter) => ({
      id: costCenter.id,
      name: costCenter.name
    })) || [];

  const columns: Array<GridEnrichedColDef> = [
    {
      field: 'name',
      headerName: t('company:COST_CENTER'),
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={`${params.id}-edit`}
          icon={<EditIcon />}
          showInMenu
          label={t('common:EDIT')}
          onClick={() => navigate(`./${params.id}`)}
          placeholder=""
        />,
        <GridActionsCellItem
          key={`${params.id}-delete`}
          icon={<DeleteIcon />}
          showInMenu
          label={t('common:DELETE')}
          onClick={() => dialog.setDialogRelevantValue(params.id as string)}
          placeholder=""
        />
      ]
    }
  ];

  useEffect(() => {
    if (!companyStore.company) return;
    const abortController = new AbortController();

    const fetchCostCenters = async () => {
      try {
        await companyStore.fetchCostCenters();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };
    fetchCostCenters();

    return () => abortController.abort();
  }, [companyStore.company]);

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('company:COST_CENTERS', { count: companyStore.costCenters?.length })}
          showActions={true}
          renderActions={() => <AddButton onClick={() => navigate('new')} />}
        />
      }
    >
      <CompanyTable
        rows={costCenters}
        columns={columns}
        onRowDoubleClick={(row) => {
          navigate(`${row.id}`);
        }}
      />
      <ConfirmDeleteDialog
        type="costCenter"
        open={dialog.dialogProps.open}
        onClose={dialog.dialogProps.onClose}
        onConfirm={() =>
          dialog?.dialogRelevantValue
            ? companyStore.deleteCostCenter(dialog.dialogRelevantValue)
            : console.error('No cost center id provided')
        }
      />
    </ContentBox>
  );
});
